import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Tooltip } from "antd";
import { fetchUnitLabels, swapUnitLabels, updateUnit } from "../../api/OrderItemUnits";
import { renderAlertMessage } from "../../helpers/common";
import { useItemsMeasureUnitsContext } from "./ItemsMeasureUnits";
import UnitHistoryPanel from "./UnitHistoryPanel";
import { openSelectOnEnter } from "./selectKeyboard";
import { toDecimalInput, toIntegerInput } from "./itemDraftHelpers";

// Shared with FlatUnitsView (its Item column and this row's own kebab/swap
// labels need the exact same "what do we call this item" rule). Slightly
// richer than ItemCard's header (which falls straight from item_name to
// "Untitled Item"): in a cross-item list, an item_description is far more
// identifying than a blank, so it's tried before the fallback.
export function itemDisplayName(item) {
  return (item && (item.item_name || item.item_description)) || "Untitled Item";
}

const KebabIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="5" r="1.7" />
    <circle cx="12" cy="12" r="1.7" />
    <circle cx="12" cy="19" r="1.7" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const PrinterIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 6 2 18 2 18 9" />
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
    <rect x="6" y="14" width="12" height="8" />
  </svg>
);

const HistoryIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 3v5h5" />
    <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" />
    <polyline points="12 7 12 12 16 14" />
  </svg>
);

// labels_pdf (base64) -> blob object URL for download/print. Duplicated from
// BulkApplyRow's downloadLabelsPdf (not exported there) since it's a small,
// self-contained helper — same convention BulkApplyRow used for modeFields.
function pdfBlobUrl(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: "application/pdf" });
  return URL.createObjectURL(blob);
}

const STATUS_LIST = [
  "Pending",
  "Received",
  "Loaded",
  "In Transit",
  "On Hand",
  "Delivered",
  "Exception",
];

function statusPillClass(status) {
  if (status === "Exception") return "exception";
  if (!status || status === "Pending") return "";
  return "progress";
}

// Booked/Re-Weighed toggle picks which pair of unit columns a cell edits.
// Piece split and storage location are mode-agnostic (one value on the unit
// regardless of the toggle), matching the mockup's renderUnitRow.
function modeFields(mode) {
  return mode === "reweigh"
    ? {
        weight: "reweigh_weight",
        weightUom: "reweigh_weight_uom",
        length: "reweigh_length",
        width: "reweigh_width",
        height: "reweigh_height",
        dimUom: "reweigh_dim_uom",
      }
    : {
        weight: "booked_weight",
        weightUom: "booked_weight_uom",
        length: "booked_length",
        width: "booked_width",
        height: "booked_height",
        dimUom: "booked_dim_uom",
      };
}

function buildDraft(unit, mode) {
  const f = modeFields(mode);
  const source = unit || {};
  return {
    piece_split: source.piece_split === null || source.piece_split === undefined ? "" : source.piece_split,
    weight: source[f.weight] === null || source[f.weight] === undefined ? "" : source[f.weight],
    weight_uom: source[f.weightUom] || "lb",
    length: source[f.length] === null || source[f.length] === undefined ? "" : source[f.length],
    width: source[f.width] === null || source[f.width] === undefined ? "" : source[f.width],
    height: source[f.height] === null || source[f.height] === undefined ? "" : source[f.height],
    dim_uom: source[f.dimUom] || "in",
    storage_location: source.storage_location || "",
    status: source.status || "Pending",
  };
}

// Numeric-aware dirty check: "40" vs "40.00" (or a value re-typed identical)
// must not count as a change, otherwise every blur would fire a needless
// PATCH. Mirrors ItemCard's NUMERIC_DRAFT_FIELDS convention.
function numsEqual(a, b) {
  const aBlank = a === "" || a === null || a === undefined;
  const bBlank = b === "" || b === null || b === undefined;
  if (aBlank && bBlank) return true;
  if (aBlank !== bBlank) return false;
  const na = parseFloat(a);
  const nb = parseFloat(b);
  return !Number.isNaN(na) && !Number.isNaN(nb) && na === nb;
}

// Scannable-units row: checkbox, Unit Label + LPN code, (Item, when rendered
// from FlatUnitsView), Piece Split, Weight (+uom), Dim LxWxH (+uom), Storage
// Location, Status pill, kebab (Task 6: download/print label, view scan
// history, swap label with another unit). Every cell edit is an independent
// sparse PATCH via updateUnit — no row-level debounce, since these are meant
// to feel like scanning/typing one field at a time, not a multi-field form.
// On success the row is replaced from the response (via onSaved) and the
// container reload() is kicked off to refresh the summary tiles / item
// rollups that depend on this unit's data.
//
// `showItemColumn`/`itemName` let FlatUnitsView reuse this exact row (per the
// plan) instead of duplicating it just to add one cell.
//
// `previewRemoved` (Frontend Task 11): this row is one of the highest-seq
// units that will be soft-deleted by the server's sync-on-save if the HUs
// draft the user is currently typing gets saved as-is — a DISPLAY-ONLY
// preview derived purely from comparing the draft to props (see UnitsTable),
// never a real state transition. It forces every write affordance on the row
// (fields, swap, save()) to behave exactly like `readOnly`, on top of
// whatever `readOnly` already is, and adds the grayed/struck styling + a
// "removed on Save" tag. Reverting the draft (or Save+reload actually
// shrinking the unit list) simply stops this prop from being passed true —
// no separate cleanup needed.
//
// `preview` (Wave A): the same object UnitsTable computed via
// computeHandlingUnitsPreview and handed to ItemCard — passed straight
// through here (null from FlatUnitsView/BulkApplyRow contexts with no item
// draft in scope) so this row can show this unit's live computed
// split/weight/dims/location instead of its stale persisted value while the
// item draft is dirty, in Booked mode only (Fix 3). QA fix wave 2 / F1: the
// piece-split input is never locked/disabled off the item's HUs/pieces —
// only the DERIVED preview value (for non-manual units) still uses this.
const UnitRow = ({
  item,
  unit,
  mode,
  checked,
  onCheck,
  onSaved,
  readOnly = false,
  showItemColumn = false,
  itemName = "",
  previewRemoved = false,
  preview = null,
  // Editable-units fix (docs/order-item-units-frontend-plan-2): `isAdded`
  // renders a not-yet-created unit (UnitsTable's buildAddedUnitSeed) —
  // `unit` is a synthetic seed object, not a real server row, so there's no
  // id to PATCH, no lpn_code, no history/labels/swap. `addedIndex` is this
  // row's position among the item's `preview.newUnitPreviews`. `deferSaves`
  // is the broader "this item's units table is previewing something Save
  // will recompute" gate ItemCard computes (preview.previewActive ||
  // preview.addCount > 0) — while it's open, EXISTING rows join added rows
  // in routing every edit into `onPendingEdit` instead of an immediate
  // `save()` PATCH, so a re-weigh/scan edit made mid-preview doesn't race a
  // value Save is about to overwrite anyway. `pendingEdits` is this specific
  // row's slice of the container's pending-edit map (field -> user-typed
  // value; never includes a field the user hasn't touched — see the
  // isAdded/deferSaves branches below, which only ever call `onPendingEdit`
  // from an onChange, never to mirror a derived default back into it).
  isAdded = false,
  // Units-during-create fix: a seed row for a pending "Add N Items" card
  // that has no server-side item at all yet (as opposed to `isAdded`'s other
  // use — a placeholder for an EXISTING item's HUs draft growing). Implies
  // `isAdded` is also true (UnitsTable only ever sets this on an added row),
  // and trims the row further per the reported bug's spec: no checkbox (no
  // real id to select), a fixed "Pending" pill instead of a status dropdown
  // (nothing has scanned yet), and "New unit"/"created on Save" labels
  // instead of a seq-numbered "Unit N"/"LPN on Save" — none of which apply
  // to the ordinary edit-flow placeholder, so that row's appearance is
  // unchanged.
  isNewItemSeed = false,
  addedIndex = null,
  deferSaves = false,
  pendingEdits = {},
  onPendingEdit = () => {},
  // Audit fix I2 (minimal-scope FlatUnitsView lock): a plain-HTML `title`
  // shown on the row while FlatUnitsView has decided this unit's parent item
  // is being edited elsewhere (Items view draft dirty, or pending unit edits
  // not yet flushed) — the caller also passes `readOnly` true alongside this
  // so the row is genuinely locked, not just visually annotated.
  rowTooltip = null,
}) => {
  const {
    reload,
    items: allItems,
    orderNumber = "",
    warehouseCode = "",
    setLiveUnitValue = () => {},
    // Task 7: counterpart called on a failed save so this unit's live
    // top-panel overlay reverts along with the row's own draft.
    clearLiveUnitValue = () => {},
    // Config-based Print label: orgSettings (to read print_label_based_on_lpn)
    // and openOrderLabelPopup (opens the shared order-level label popup rendered
    // by the container).
    orgSettings: ctxOrgSettings = {},
    openOrderLabelPopup = () => {},
  } = useItemsMeasureUnitsContext();

  // print_label_based_on_lpn: ON → keep printing this unit's LPN label
  // (existing behavior); OFF → open the order-level label-template popup (same
  // as the order-details "Print Labels") for the whole order.
  const lpnLabelMode =
    ctxOrgSettings &&
    ctxOrgSettings.items_measure_units_screen === "true" &&
    ctxOrgSettings.print_label_based_on_lpn === "true";

  // Live LPN preview for a not-yet-saved (isAdded) row: mirrors the server's
  // "<warehouse_code>-<order_number>-<seq>" format so the user sees the LPN the
  // unit will get on Save. Uses the same seq shown in the row's "Unit N" label
  // to stay consistent. Approximate — the server's cross-order collision
  // fallback can't be replicated client-side, and it needs an order number, so
  // create-flow seed rows (isNewItemSeed, no order yet) keep their placeholder.
  const lpnPreview =
    orderNumber && unit && unit.seq != null
      ? `${warehouseCode || "NA"}-${orderNumber}-${String(unit.seq).padStart(3, "0")}`
      : null;
  const f = modeFields(mode);
  // Every existing `readOnly` disable-check below reads this instead, so a
  // preview-removed row locks solid without duplicating each disabled={...}
  // check into a two-condition expression.
  const previewLocked = readOnly || previewRemoved;
  // A previewRemoved row is about to be soft-deleted on Save — it stays
  // fully locked regardless of deferSaves (matches the doc's "keep
  // previewRemoved rows fully locked as they are today"). Otherwise, added
  // rows are ALWAYS in deferred mode (there is no real unit id to PATCH
  // immediately), and existing rows join them whenever the item's own
  // deferSaves gate is open.
  const useDeferred = !previewLocked && (isAdded || deferSaves);

  const [draft, setDraft] = useState(() => buildDraft(unit, mode));
  const [menuOpen, setMenuOpen] = useState(false);
  // Viewport coordinates for the portaled kebab panel (set on open).
  const [menuPos, setMenuPos] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [labelBusy, setLabelBusy] = useState(false);
  const [swapBusy, setSwapBusy] = useState(false);
  const isMounted = useRef(true);
  const unitRef = useRef(unit);
  unitRef.current = unit;
  const menuWrapRef = useRef(null);
  const menuPanelRef = useRef(null);
  const dimsCellRef = useRef(null);
  // B2: true for as long as the "Swap label with…" <select> itself is
  // focused — see the outside-click/scroll-close effect below for why.
  const swapSelectOpenRef = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Re-seed whenever a fresh unit (post-save replacement or post-reload) or
  // the mode toggle arrives; server truth always wins. Skipped for `isAdded`
  // rows: UnitsTable rebuilds its synthetic seed object every render (a new
  // reference each time, since it always reflects the live preview), so this
  // effect would otherwise re-fire every render for no reason — added rows
  // never read `draft` for display anyway (see the isAdded/deferSaves
  // branches below), only the persisted/clean-mode branches do.
  useEffect(() => {
    if (isAdded) return;
    setDraft(buildDraft(unit, mode));
  }, [unit, mode, isAdded]);

  // B2 (debug-first fix): "Swap label with…" lives in this portaled panel as
  // a native <select> (unit-menu-select below). Root cause of "swap label
  // not working at all": a native <select>'s own dropdown popup is browser/
  // OS chrome, not a normal descendant of menuPanelRef's DOM subtree — on
  // some browsers/platforms, opening it or picking an option can still
  // deliver a `mousedown` (or a `scroll`/`resize`, e.g. a mobile viewport
  // resize when the native picker appears) that this panel sees as
  // "outside," closing (and portal-unmounting) the menu — and the <select>
  // along with it — before the pending `change` event ever reaches
  // handleSwapSelect. The swap then silently never fires; confirmed the
  // backend endpoint itself has no bug here (rails runner trace: seq/
  // unit_label/lpn_code swap correctly). Fix: track whether the swap
  // <select> itself currently has focus (set/cleared by its own onFocus/
  // onBlur below) — true for exactly as long as its dropdown can plausibly
  // be open — and suppress BOTH close paths while it is, so nothing this
  // select's own native popup does can tear the panel down out from under a
  // pending selection. A genuine click on something else still closes the
  // panel: the click that lands elsewhere first blurs the select (clearing
  // swapSelectOpenRef), and the very next outside interaction closes it as
  // before.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleOutside = (e) => {
      if (swapSelectOpenRef.current) return;
      const inAnchor = menuWrapRef.current && menuWrapRef.current.contains(e.target);
      const inPanel = menuPanelRef.current && menuPanelRef.current.contains(e.target);
      if (!inAnchor && !inPanel) setMenuOpen(false);
    };
    // Fixed-position panels don't follow their anchor: any scroll (page or
    // the table's own scroll box — hence capture phase) or resize would leave
    // the panel floating detached, so just close it like native menus do.
    const handleScrollOrResize = () => {
      if (swapSelectOpenRef.current) return;
      setMenuOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [menuOpen]);

  // Anchor the fixed-position portal panel to the kebab button: below it,
  // right-aligned (the mockup's `top: calc(100% + 4px); right: 0`).
  const toggleMenu = () => {
    setMenuOpen((prev) => {
      if (!prev && menuWrapRef.current) {
        const rect = menuWrapRef.current.getBoundingClientRect();
        setMenuPos({
          top: rect.bottom + 4,
          right: Math.max(window.innerWidth - rect.right, 8),
        });
      }
      return !prev;
    });
  };

  // Grouped "swap label with…" options: every OTHER item's units (mockup's
  // renderSwapOptions). `allItems` comes from the container's full items
  // list (context), so this works identically whether the row is rendered
  // inside a per-item UnitsTable or the cross-item FlatUnitsView.
  const swapGroups = useMemo(() => {
    return (allItems || [])
      .filter((otherItem) => !item || otherItem.id !== item.id)
      .map((otherItem) => ({
        itemId: otherItem.id,
        itemLabel: itemDisplayName(otherItem),
        units: (otherItem.units || []).map((u) => ({
          id: u.id,
          unit_label: u.unit_label,
          lpn_code: u.lpn_code,
        })),
      }))
      .filter((group) => group.units.length > 0);
  }, [allItems, item]);

  const save = (attrs) => {
    // Every cell already routes to onPendingEdit while useDeferred is open;
    // this is the backstop, so a handler added later that forgets the branch
    // degrades to "edit not saved yet" rather than PATCHing a value Save is
    // about to re-derive (or, for an added row, a unit id that doesn't exist).
    if (previewLocked || useDeferred) return;
    updateUnit(unit.id, attrs).then((result) => {
      if (!isMounted.current) return;
      if (result.success) {
        if (onSaved) onSaved(result.unit);
        // IM2: these fields were mirrored into the container's pending-edit
        // channel on every keystroke (for the live mismatch warning). Now that
        // they're persisted, clear them from that channel — otherwise the entry
        // lingers, keeping the card's dirty border lit AND making a later
        // Save-All re-PATCH this (now potentially stale) value over any
        // server-side rebalance. A field set to `undefined` is removed from the
        // pending set; keys that were never mirrored (e.g. reweigh weight) are a
        // harmless no-op.
        const clearPending = {};
        Object.keys(attrs).forEach((k) => { clearPending[k] = undefined; });
        onPendingEdit(clearPending);
        reload();
      } else if (result.status === 404) {
        // B-M6: this unit is already gone server-side (e.g. its item was
        // re-saved/recreated elsewhere while this row was on screen) — reload
        // instead of toasting a confusing "not found" for an edit the user
        // just made; the row disappears once the refreshed items/units land.
        reload();
      } else {
        renderAlertMessage(result.errors);
        // Roll the draft back to the last known-good server value so a
        // failed save doesn't leave stale/rejected text in the input.
        setDraft(buildDraft(unitRef.current, mode));
        // Task 7: also drop this unit's live top-panel overlay (set by
        // handleWeightChange/handleDimChange/uom changes below) so a
        // rejected DIM/weight value stops feeding the Booked/Re-Weight/
        // Re-Dim/Chargeable rollups — they fall back to the persisted
        // value the draft revert above just restored.
        clearLiveUnitValue(unit.id);
      }
    });
  };

  const selectOnDefaultFocus = (isDefault) => (e) => {
    if (isDefault) e.target.select();
  };

  // React 16 pools synthetic events: e.target is null by the time a deferred
  // functional updater runs, so the value must be captured BEFORE setDraft.
  const handlePieceSplitChange = (e) => {
    const value = e.target.value;
    setDraft((prev) => ({ ...prev, piece_split: value }));
    // Live warning (reported: "if i change piece split, then it's not showing
    // warning instantly. after that i should click on somewhere to show
    // that"): the piece-split mismatch is computed in ItemCard by
    // computeLiveMismatches, which reads THIS unit's value only from the
    // persisted `units` prop or from the container's pending-edit override —
    // never from this row's local `draft`. Outside deferred mode the input
    // used to update `draft` alone, so the sum (and its warning) didn't move
    // until blur→save→reload refreshed `units`. Mirroring every keystroke
    // into the same pending-edit channel the deferred branch uses makes the
    // warning recompute on each keystroke. The value is still PATCHed on blur
    // (handlePieceSplitBlur); the override simply equals the persisted value
    // after the reload lands, so the sum stays correct.
    onPendingEdit({ piece_split: value });
  };
  const handlePieceSplitBlur = () => {
    if (numsEqual(draft.piece_split, unit.piece_split)) return;
    save({ piece_split: draft.piece_split });
  };

  const handleWeightChange = (e) => {
    const value = toDecimalInput(e.target.value);
    setDraft((prev) => ({ ...prev, weight: value }));
    // Live top-panel rollups: push the typed weight into the display-only live
    // channel (keyed by the current mode's field) so Booked/Re-Weight and
    // Chargeable update as you type. See ItemsMeasureUnits.setLiveUnitValue.
    setLiveUnitValue(unit.id, { [f.weight]: value, [f.weightUom]: draft.weight_uom });
    // Live weight_mismatch warning — BOOKED mode only (overriddenWeightLb reads
    // only booked_weight; a Re-Weigh edit has no bearing on the item-total
    // weight mismatch, and mirroring it risks a redundant history record when
    // the pending edit is flushed at global Save).
    if (mode !== "reweigh") {
      onPendingEdit({ booked_weight: value, booked_weight_uom: draft.weight_uom });
    }
  };
  const handleWeightBlur = () => {
    if (numsEqual(draft.weight, unit[f.weight]) && draft.weight_uom === (unit[f.weightUom] || "lb")) return;
    save({ [f.weight]: draft.weight, [f.weightUom]: draft.weight_uom });
  };
  const handleWeightUomChange = (e) => {
    const value = e.target.value;
    setDraft((prev) => ({ ...prev, weight_uom: value }));
    setLiveUnitValue(unit.id, { [f.weightUom]: value });
    if (value === (unit[f.weightUom] || "lb") && numsEqual(draft.weight, unit[f.weight])) return;
    save({ [f.weight]: draft.weight, [f.weightUom]: value });
  };

  const handleDimChange = (key) => (e) => {
    const value = toDecimalInput(e.target.value);
    setDraft((prev) => ({ ...prev, [key]: value }));
    // key is "length" | "width" | "height"; map to the mode's real field so
    // the live Dim/Re-Dim/Chargeable rollups track the keystroke.
    setLiveUnitValue(unit.id, { [f[key]]: value });
  };
  const handleDimUomChange = (e) => {
    const value = e.target.value;
    setDraft((prev) => ({ ...prev, dim_uom: value }));
    setLiveUnitValue(unit.id, { [f.dimUom]: value });
    // No save here — the group blur below persists the whole triple once.
  };
  // L/W/H/uom are three sub-fields of ONE logical measurement. Saving on each
  // field's individual blur fired three PATCHes for a single "1 x 2 x 3" entry
  // — and since every re-weigh PATCH appends a measurement-history record, the
  // user saw three partial rows (1x0x0, 1x2x0, 1x2x3). Instead, save ONCE when
  // focus actually leaves the whole dims cell (moving between the L/W/H inputs
  // and the uom select stays inside the cell → no save), so one entry = one
  // PATCH = one history record.
  const handleDimsGroupBlur = (e) => {
    if (dimsCellRef.current && e.relatedTarget && dimsCellRef.current.contains(e.relatedTarget)) return;
    const unchanged =
      numsEqual(draft.length, unit[f.length]) &&
      numsEqual(draft.width, unit[f.width]) &&
      numsEqual(draft.height, unit[f.height]) &&
      draft.dim_uom === (unit[f.dimUom] || "in");
    if (unchanged) return;
    save({
      [f.length]: draft.length,
      [f.width]: draft.width,
      [f.height]: draft.height,
      [f.dimUom]: draft.dim_uom,
    });
  };

  const handleLocationChange = (e) => {
    const value = e.target.value;
    setDraft((prev) => ({ ...prev, storage_location: value }));
  };
  const handleLocationBlur = () => {
    if ((draft.storage_location || "") === (unit.storage_location || "")) return;
    save({ storage_location: draft.storage_location });
  };

  const handleStatusChange = (e) => {
    const value = e.target.value;
    setDraft((prev) => ({ ...prev, status: value }));
    if (value === (unit.status || "Pending")) return;
    save({ status: value });
  };

  const weightIsDefault = mode === "booked" && !unit.booked_weight_manual;
  const dimIsDefault = mode === "booked" && !unit.booked_dim_manual;
  const splitIsDefault = !unit.piece_split_manual;
  const locationIsDefault = !unit.storage_location_manual;

  // Wave A / Fix 3: this row's live computed preview values, gated on Booked
  // mode (Re-Weighed is untouched — those columns are actual measurements,
  // never derived) and on the item draft actually being dirty on a field the
  // server's Save-time re-derivation reads (`preview.previewActive`, see
  // UnitsTable's relevantFieldsDirty). `unitPreview` is undefined for a row
  // with no computed preview (e.g. this exact unit id isn't in
  // unitPreviewsById because it's preview-removed) — every *Previewing flag
  // below already folds that in via `!!unitPreview`.
  //
  // Each field only previews when its OWN manual flag is false, mirroring
  // apply_piece_splits/apply_booked_defaults/apply_location_defaults each
  // skipping manually-set units independently (order_item_unit_sync_service.rb:
  // 172/197/201/219) — a manually-set weight, say, stays real/editable even
  // while dims and piece split on the very same row are previewing.
  // Piece split and storage location are single mode-agnostic values on the
  // unit (see modeFields above — only weight and dims come in booked/reweigh
  // pairs), and the server derives them without regard to any mode, so their
  // preview must show in BOTH modes. Weight/dims stay booked-only, enforced by
  // weightIsDefault/dimIsDefault which already carry the `mode === "booked"`
  // test — switching to Re-Weighed therefore drops the weight/dim previews on
  // its own without dropping the split/location ones with them.
  // Audit fix C1: an added/seed row is only ever rendered because UnitsTable
  // already decided to show it (index < preview.addCount) — gating its OWN
  // preview value again on `previewActive` meant an untouched legacy item
  // with zero persisted units (addCount > 0 purely from the row-count
  // fallback math, but previewActive false — see UnitsTable's
  // computeHandlingUnitsPreview B1/C1 comments) rendered a seed row with no
  // derived split/weight/dims at all. newUnitPreviews is now built whenever
  // addCount > 0 (not just previewActive), so an added row reads it
  // unconditionally; only the EXISTING-row branch still needs the
  // previewActive gate (a real persisted unit's own value should only be
  // overridden by a preview once something the server would re-derive has
  // actually changed).
  const unitPreview = isAdded
    ? preview && preview.newUnitPreviews
      ? preview.newUnitPreviews[addedIndex]
      : null
    : preview && preview.previewActive && preview.unitPreviewsById
    ? preview.unitPreviewsById[unit.id]
    : null;
  // Wave A / Fix 3: recalculated live from the dirty draft, but — QA fix
  // wave 2 / F1 — only for NON-manual units (splitIsDefault). The server's
  // apply_piece_splits (order_item_unit_sync_service.rb) once again honors
  // piece_split_manual, so a manually-typed split is never going to be
  // rewritten by the next Save; showing this row's own real, editable value
  // instead of a formula preview keeps the cell honest about that.
  const splitPreviewValue = unitPreview && splitIsDefault ? unitPreview.pieceSplit : null;
  const splitPreviewing = splitPreviewValue !== null;
  const weightPreviewing = !!unitPreview && weightIsDefault;
  const dimsPreviewing = !!unitPreview && dimIsDefault;
  const locationPreviewing = !!unitPreview && locationIsDefault && unitPreview.storageLocation != null;

  // ---------------------------------------------------------------------
  // Editable-units fix: display-value resolution for `useDeferred` cells
  // (added rows always; existing rows while the item is previewing). Each
  // field's value is `pendingEdits[field] ?? liveDerivedDefault ??
  // persisted`, straight off props — no local `draft` state involved, so a
  // parent-driven preview recompute (e.g. the user still typing in the
  // ITEM's own Pieces/HUs box) can keep updating an untouched field's shown
  // default live, exactly like the old disabled "Recalculated on Save" cells
  // did, right up until the user types into THIS field — the moment
  // `pendingEdits[field]` is set (by this row's own onChange below), it wins
  // permanently for the rest of this row's life, matching "keep is-default
  // styling until the user types, then drop it." `unit[...]` is only ever
  // read here for an EXISTING row (isAdded rows have no persisted value to
  // fall back to — "" instead).
  const persistedOrBlank = (value) => (isAdded ? "" : value ?? "");

  const splitFieldValue =
    pendingEdits.piece_split !== undefined
      ? pendingEdits.piece_split
      : splitPreviewValue !== null
      ? splitPreviewValue
      : persistedOrBlank(unit.piece_split);
  const splitShowsDefault = pendingEdits.piece_split === undefined && (isAdded || splitIsDefault);

  const weightFieldValue =
    pendingEdits[f.weight] !== undefined
      ? pendingEdits[f.weight]
      : weightPreviewing
      ? unitPreview.weight.toFixed(2)
      : persistedOrBlank(unit[f.weight]);
  const weightUomFieldValue =
    pendingEdits[f.weightUom] !== undefined
      ? pendingEdits[f.weightUom]
      : weightPreviewing
      ? unitPreview.weightUom
      : isAdded
      ? "lb"
      : unit[f.weightUom] || "lb";
  const weightShowsDefault =
    pendingEdits[f.weight] === undefined && pendingEdits[f.weightUom] === undefined && (isAdded ? mode === "booked" : weightIsDefault);

  const lengthFieldValue =
    pendingEdits[f.length] !== undefined
      ? pendingEdits[f.length]
      : dimsPreviewing
      ? unitPreview.length
      : persistedOrBlank(unit[f.length]);
  const widthFieldValue =
    pendingEdits[f.width] !== undefined
      ? pendingEdits[f.width]
      : dimsPreviewing
      ? unitPreview.width
      : persistedOrBlank(unit[f.width]);
  const heightFieldValue =
    pendingEdits[f.height] !== undefined
      ? pendingEdits[f.height]
      : dimsPreviewing
      ? unitPreview.height
      : persistedOrBlank(unit[f.height]);
  const dimUomFieldValue =
    pendingEdits[f.dimUom] !== undefined
      ? pendingEdits[f.dimUom]
      : dimsPreviewing
      ? unitPreview.dimUom
      : isAdded
      ? "in"
      : unit[f.dimUom] || "in";
  const dimsShowDefault =
    pendingEdits[f.length] === undefined &&
    pendingEdits[f.width] === undefined &&
    pendingEdits[f.height] === undefined &&
    pendingEdits[f.dimUom] === undefined &&
    (isAdded ? mode === "booked" : dimIsDefault);

  const locationFieldValue =
    pendingEdits.storage_location !== undefined
      ? pendingEdits.storage_location
      : locationPreviewing
      ? unitPreview.storageLocation
      : persistedOrBlank(unit.storage_location);
  const locationShowsDefault = pendingEdits.storage_location === undefined && (isAdded || locationIsDefault);

  const statusFieldValue =
    pendingEdits.status !== undefined ? pendingEdits.status : isAdded ? "Pending" : unit.status || "Pending";

  // Dims/weight are committed as one grouped PATCH today (a uom change must
  // carry the sibling number(s) along, and vice versa) — mirrored here so a
  // deferred edit lands in pendingEdits with the exact same shape `save()`
  // would have PATCHed, which is what makes the CRITICAL "only typed fields
  // become pending edits, and they apply as *_manual on Save" contract work:
  // apply_unit_changes sets the *_manual flag for every key present in the
  // payload, so these committers must never include a field the user didn't
  // actually touch this round.
  const commitWeight = (overrides) =>
    onPendingEdit({
      [f.weight]: overrides.weight !== undefined ? overrides.weight : weightFieldValue,
      [f.weightUom]: overrides.weightUom !== undefined ? overrides.weightUom : weightUomFieldValue,
    });
  const commitDims = (overrides) =>
    onPendingEdit({
      [f.length]: overrides.length !== undefined ? overrides.length : lengthFieldValue,
      [f.width]: overrides.width !== undefined ? overrides.width : widthFieldValue,
      [f.height]: overrides.height !== undefined ? overrides.height : heightFieldValue,
      [f.dimUom]: overrides.dimUom !== undefined ? overrides.dimUom : dimUomFieldValue,
    });

  const handleDownloadLabel = () => {
    if (labelBusy) return;
    // Config OFF: no per-unit LPN labels — open the order-level label-template
    // popup (order-details "Print Labels") for the whole order instead.
    if (!lpnLabelMode) {
      setMenuOpen(false);
      openOrderLabelPopup();
      return;
    }
    setLabelBusy(true);
    fetchUnitLabels([unit.id])
      .then((result) => {
        if (!isMounted.current) return;
        if (result.success) {
          const url = pdfBlobUrl(result.labels_pdf);
          const anchor = document.createElement("a");
          anchor.href = url;
          anchor.download = result.file_name || `${unit.unit_label || "label"}.pdf`;
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
          URL.revokeObjectURL(url);
        } else {
          renderAlertMessage(result.errors);
        }
      })
      .finally(() => {
        if (isMounted.current) {
          setLabelBusy(false);
          setMenuOpen(false);
        }
      });
  };

  const handlePrintLabel = () => {
    if (labelBusy) return;
    // Config OFF: no per-unit LPN labels — open the order-level label-template
    // popup (order-details "Print Labels") for the whole order instead.
    if (!lpnLabelMode) {
      setMenuOpen(false);
      openOrderLabelPopup();
      return;
    }
    setLabelBusy(true);
    fetchUnitLabels([unit.id])
      .then((result) => {
        if (!isMounted.current) return;
        if (result.success) {
          const url = pdfBlobUrl(result.labels_pdf);
          const win = window.open(url, "_blank");
          if (win) {
            const tryPrint = () => {
              try {
                win.print();
              } catch (err) {
                // Ignore: some browsers restrict scripting a native PDF
                // viewer tab; the user can still print from that tab
                // manually.
              }
            };
            win.onload = tryPrint;
            // Fallback: onload doesn't reliably fire when a browser opens a
            // PDF directly in its built-in viewer rather than as an HTML
            // document, per the plan's "mirror the mockup's spirit" note.
            // The blob URL is revoked shortly after the fallback print
            // attempt — by then the viewer tab has loaded the document, so
            // the URL is no longer needed (mirrors handleDownloadLabel's
            // revoke). Deliberately NOT tied to isMounted: the tab keeps
            // printing fine even if this row unmounted meanwhile.
            setTimeout(() => {
              tryPrint();
              setTimeout(() => URL.revokeObjectURL(url), 10000);
            }, 800);
          } else {
            URL.revokeObjectURL(url);
            renderAlertMessage(["Unable to open a new window to print the label."]);
          }
        } else {
          renderAlertMessage(result.errors);
        }
      })
      .finally(() => {
        if (isMounted.current) {
          setLabelBusy(false);
          setMenuOpen(false);
        }
      });
  };

  const handleToggleHistory = () => {
    setHistoryOpen((prev) => !prev);
    setMenuOpen(false);
  };

  const handleSwapSelect = (e) => {
    // Swap mutates server state, so it is gated like every other write
    // affordance on the row (the whole swap section is also hidden when
    // readOnly; this is defense in depth).
    if (previewLocked) return;
    const targetUnitId = e.target.value;
    if (!targetUnitId || swapBusy) return;
    setSwapBusy(true);
    swapUnitLabels(unit.id, targetUnitId)
      .then((result) => {
        if (!isMounted.current) return;
        if (result.success) {
          reload();
        } else {
          renderAlertMessage(result.errors);
        }
      })
      .finally(() => {
        if (isMounted.current) {
          setSwapBusy(false);
          setMenuOpen(false);
        }
      });
  };

  // Status column removed: one fewer column. The history panel renders a
  // leading <td/> then <td colSpan>, so colSpan = (total columns − 1).
  const historyColSpan = showItemColumn ? 7 : 6;

  // Read-only cell text (Figma view frame 4646-60202): em-dash for a blank
  // value so an empty cell reads as intentionally-empty, not broken.
  const roText = (v) => (v === "" || v === null || v === undefined ? "—" : v);

  return (
    <React.Fragment>
    <tr
      className={previewRemoved ? "preview-removed-row" : isAdded ? "preview-new-row" : undefined}
      title={rowTooltip || undefined}
    >
      <td>
        {/* Units-during-create fix: no server identity exists yet for a
            brand-new item's seed row — omit the checkbox entirely rather
            than offering a disabled/unchecked one. */}
        {!isNewItemSeed && (
          <input
            type="checkbox"
            className="rowcheck"
            checked={isAdded || previewRemoved ? false : checked}
            // Selection is allowed in read-only view too (user request: "in
            // view mode also, we should allow scannable units to select") — it
            // drives the read-only bulk bar's Download-labels action. Still
            // blocked for preview-removed / not-yet-created rows.
            disabled={previewRemoved || isAdded}
            onChange={() => {
              if (!isAdded) onCheck(unit.id);
            }}
          />
        )}
      </td>
      <td className="lpn-cell">
        <div className="unit-label">{isNewItemSeed ? `Unit ${(addedIndex ?? 0) + 1}` : unit.unit_label}</div>
        <div className="unit-lpn-code">
          {isNewItemSeed
            ? "created on Save"
            : isAdded
            ? (lpnPreview || "LPN on Save")
            : unit.lpn_code}
        </div>
        {previewRemoved && <span className="preview-tag preview-tag-removed">removed on Save</span>}
      </td>
      {showItemColumn && (
        <td className="flat-item-cell" title={itemName}>
          {itemName}
        </td>
      )}
      <td>
        {readOnly ? (
          // Read-only view (Figma view frame 4646-60202): every editable cell
          // renders as plain text, not a disabled input.
          <span className="cell-ro">{roText(draft.piece_split)}</span>
        ) : useDeferred ? (
          // Editable-units fix: previously a disabled "Recalculated on Save"
          // cell (or, for an added row, static dashed text) — now a real
          // input pre-filled with the same live-derived value, editable
          // immediately. Committing routes to onPendingEdit (accumulated,
          // flushed at Save) instead of an immediate PATCH.
          <input
            type="text"
            inputMode="numeric"
            className={`split-input ${splitShowsDefault ? "is-default" : ""}`}
            value={splitFieldValue}
            onFocus={selectOnDefaultFocus(splitShowsDefault)}
            onChange={(e) => onPendingEdit({ piece_split: toIntegerInput(e.target.value) })}
          />
        ) : splitPreviewing ? (
          // Wave A / Fix 3: recalculated live from the dirty draft (see
          // UnitsTable's apply_piece_splits mirror) — read-only while
          // previewing so a manual edit here can't race a value that's about
          // to be overwritten by Save's own re-derivation anyway.
          <Tooltip title="Recalculated on Save">
            <span>
              <input
                type="text"
                inputMode="numeric"
                className="split-input is-default"
                value={unitPreview.pieceSplit}
                disabled
                readOnly
              />
            </span>
          </Tooltip>
        ) : (
          <input
            type="text"
            inputMode="numeric"
            className={`split-input ${splitIsDefault ? "is-default" : ""}`}
            value={draft.piece_split}
            disabled={previewLocked}
            onFocus={selectOnDefaultFocus(splitIsDefault)}
            onChange={handlePieceSplitChange}
            onBlur={handlePieceSplitBlur}
          />
        )}
      </td>
      <td>
        {readOnly ? (
          <span className="cell-ro">
            {draft.weight === "" || draft.weight === null || draft.weight === undefined
              ? "—"
              : `${draft.weight} ${draft.weight_uom || "lb"}`}
          </span>
        ) : useDeferred ? (
          <div className="unit-input">
            <input
              type="text"
              inputMode="decimal"
              className={weightShowsDefault ? "is-default" : ""}
              value={weightFieldValue}
              onFocus={selectOnDefaultFocus(weightShowsDefault)}
              onChange={(e) => commitWeight({ weight: toDecimalInput(e.target.value) })}
            />
            <select
              value={weightUomFieldValue}
              onChange={(e) => commitWeight({ weightUom: e.target.value })}
              onKeyDown={openSelectOnEnter}
            >
              {["lb", "kg"].map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
        ) : weightPreviewing ? (
          <Tooltip title="Recalculated on Save">
            <span>
              <div className="unit-input">
                <input
                  type="text"
                  inputMode="decimal"
                  className="is-default"
                  value={unitPreview.weight.toFixed(2)}
                  disabled
                  readOnly
                />
                <select value={unitPreview.weightUom} disabled>
                  {["lb", "kg"].map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </div>
            </span>
          </Tooltip>
        ) : (
          <div className="unit-input">
            <input
              type="text"
              inputMode="decimal"
              className={weightIsDefault ? "is-default" : ""}
              value={draft.weight}
              disabled={previewLocked}
              onFocus={selectOnDefaultFocus(weightIsDefault)}
              onChange={handleWeightChange}
              onBlur={handleWeightBlur}
            />
            <select
              value={draft.weight_uom}
              disabled={previewLocked}
              onChange={handleWeightUomChange}
              onKeyDown={openSelectOnEnter}
            >
              {["lb", "kg"].map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
        )}
      </td>
      <td>
        {readOnly ? (
          <span className="cell-ro">
            {`${draft.length || 0}×${draft.width || 0}×${draft.height || 0} ${draft.dim_uom || "in"}`}
          </span>
        ) : useDeferred ? (
          <div className="dims-cell">
            <input
              type="text"
              inputMode="decimal"
              className={dimsShowDefault ? "is-default" : ""}
              value={lengthFieldValue}
              onFocus={selectOnDefaultFocus(dimsShowDefault)}
              onChange={(e) => commitDims({ length: toDecimalInput(e.target.value) })}
            />
            <span className="x">&times;</span>
            <input
              type="text"
              inputMode="decimal"
              className={dimsShowDefault ? "is-default" : ""}
              value={widthFieldValue}
              onFocus={selectOnDefaultFocus(dimsShowDefault)}
              onChange={(e) => commitDims({ width: toDecimalInput(e.target.value) })}
            />
            <span className="x">&times;</span>
            <input
              type="text"
              inputMode="decimal"
              className={dimsShowDefault ? "is-default" : ""}
              value={heightFieldValue}
              onFocus={selectOnDefaultFocus(dimsShowDefault)}
              onChange={(e) => commitDims({ height: toDecimalInput(e.target.value) })}
            />
            <select
              value={dimUomFieldValue}
              onChange={(e) => commitDims({ dimUom: e.target.value })}
              onKeyDown={openSelectOnEnter}
            >
              {["in", "cm"].map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
        ) : dimsPreviewing ? (
          <Tooltip title="Recalculated on Save">
            <span>
              <div className="dims-cell">
                <input type="text" inputMode="decimal" className="is-default" value={unitPreview.length} disabled readOnly />
                <span className="x">&times;</span>
                <input type="text" inputMode="decimal" className="is-default" value={unitPreview.width} disabled readOnly />
                <span className="x">&times;</span>
                <input type="text" inputMode="decimal" className="is-default" value={unitPreview.height} disabled readOnly />
                <select value={unitPreview.dimUom} disabled>
                  {["in", "cm"].map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </div>
            </span>
          </Tooltip>
        ) : (
          <div className="dims-cell" ref={dimsCellRef} onBlur={handleDimsGroupBlur}>
            <input
              type="text"
              inputMode="decimal"
              className={dimIsDefault ? "is-default" : ""}
              value={draft.length}
              disabled={previewLocked}
              onFocus={selectOnDefaultFocus(dimIsDefault)}
              onChange={handleDimChange("length")}
            />
            <span className="x">&times;</span>
            <input
              type="text"
              inputMode="decimal"
              className={dimIsDefault ? "is-default" : ""}
              value={draft.width}
              disabled={previewLocked}
              onFocus={selectOnDefaultFocus(dimIsDefault)}
              onChange={handleDimChange("width")}
            />
            <span className="x">&times;</span>
            <input
              type="text"
              inputMode="decimal"
              className={dimIsDefault ? "is-default" : ""}
              value={draft.height}
              disabled={previewLocked}
              onFocus={selectOnDefaultFocus(dimIsDefault)}
              onChange={handleDimChange("height")}
            />
            <select
              value={draft.dim_uom}
              disabled={previewLocked}
              onChange={handleDimUomChange}
              onKeyDown={openSelectOnEnter}
            >
              {["in", "cm"].map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
        )}
      </td>
      <td>
        {readOnly ? (
          <span className="cell-ro">{roText(draft.storage_location)}</span>
        ) : useDeferred ? (
          <input
            type="text"
            className={`loc-input ${locationShowsDefault ? "is-default" : ""}`}
            value={locationFieldValue}
            onFocus={selectOnDefaultFocus(locationShowsDefault)}
            onChange={(e) => onPendingEdit({ storage_location: e.target.value })}
          />
        ) : locationPreviewing ? (
          <Tooltip title="Recalculated on Save">
            <span>
              <input type="text" className="loc-input is-default" value={unitPreview.storageLocation} disabled readOnly />
            </span>
          </Tooltip>
        ) : (
          <input
            type="text"
            className={`loc-input ${locationIsDefault ? "is-default" : ""}`}
            value={draft.storage_location}
            disabled={previewLocked}
            onFocus={selectOnDefaultFocus(locationIsDefault)}
            onChange={handleLocationChange}
            onBlur={handleLocationBlur}
          />
        )}
      </td>
      {/* Status column is hidden (product decision) — the status still lives on
          the unit and is changed from the row's ⋮ menu below. */}
      <td className="unit-menu-cell">
        {/* Editable-units fix: an added row has no real unit yet — no
            labels/history/swap exist for it, so the kebab menu itself is
            hidden entirely rather than offered disabled. */}
        {!isAdded && (
          <div className="unit-menu-wrap" ref={menuWrapRef}>
            <button
              type="button"
              className="icon-btn"
              title="More options"
              onClick={toggleMenu}
            >
              <KebabIcon />
            </button>
            {menuOpen && menuPos && createPortal(
              <div
                className="imu-unit-menu-portal"
                ref={menuPanelRef}
                style={{ position: "fixed", top: menuPos.top, right: menuPos.right }}
              >
                <button type="button" className="unit-menu-item" disabled={labelBusy} onClick={handleDownloadLabel}>
                  <DownloadIcon /> Download label
                </button>
                <button type="button" className="unit-menu-item" disabled={labelBusy} onClick={handlePrintLabel}>
                  <PrinterIcon /> Print label
                </button>
                {/* Wave A / Fix 5 (P4): the mockup hides "View history" whenever
                    a unit has no scan log yet. That's intentionally NOT
                    replicated here — this panel (UnitHistoryPanel) has its own
                    empty state, so always offering the menu item is a
                    deliberate improvement over the mockup, not an oversight. */}
                <button type="button" className="unit-menu-item" onClick={handleToggleHistory}>
                  <HistoryIcon /> {historyOpen ? "Hide" : "View"} scan history
                </button>
                {/* Status moved here now that its table column is hidden. Not
                    offered for a not-yet-created seed row (nothing to change
                    yet) or a locked/preview row. */}
                {!previewLocked && !isNewItemSeed && (
                  <React.Fragment>
                    <div className="unit-menu-divider" />
                    <div className="unit-menu-label">Change status</div>
                    <select
                      className="unit-menu-select"
                      value={useDeferred ? statusFieldValue : draft.status}
                      disabled={previewLocked}
                      onChange={useDeferred ? (e) => onPendingEdit({ status: e.target.value }) : handleStatusChange}
                      onKeyDown={openSelectOnEnter}
                    >
                      {STATUS_LIST.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </React.Fragment>
                )}
                {!previewLocked && swapGroups.length > 0 && (
                  <React.Fragment>
                    <div className="unit-menu-divider" />
                    <div className="unit-menu-label">Swap label with</div>
                    <select
                      className="unit-menu-select"
                      value=""
                      disabled={swapBusy}
                      onFocus={() => {
                        swapSelectOpenRef.current = true;
                      }}
                      onBlur={() => {
                        swapSelectOpenRef.current = false;
                      }}
                      onChange={handleSwapSelect}
                      onKeyDown={openSelectOnEnter}
                    >
                      <option value="">Choose a unit&hellip;</option>
                      {swapGroups.map((group) => (
                        <optgroup key={group.itemId} label={group.itemLabel}>
                          {group.units.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.unit_label} ({u.lpn_code})
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </React.Fragment>
                )}
              </div>,
              document.body
            )}
          </div>
        )}
      </td>
    </tr>
    {historyOpen && !isAdded && (
      <UnitHistoryPanel unitId={unit.id} unitLabel={unit.unit_label} colSpan={historyColSpan} />
    )}
    </React.Fragment>
  );
};

// Memoized: the grid renders many rows, so skip re-rendering a row whose props
// didn't change (e.g. a keystroke in another row/card).
export default React.memo(UnitRow);
